import type { Proxy } from '#~/agent/index.js';
import * as DialIndeterminate from '@studio/design-system/dial-indeterminate';
import { generateId } from '@studio/models';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { Control } from '@tryiris-ai/common.protojs/agent/v1/agent';
import {
  DeviceRegistryCommandFunctionType,
  DeviceRegistryCommandKey,
  DeviceRegistryInterfaceItemControlType
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { noop } from 'lodash-es';
import type { ReactElement } from 'react';
import type { Device } from '@studio/models/device';

interface DeviceSettingDialProps {
  agent?: T_Maybe<Proxy>;
  className: string;
  cmdKey: DeviceRegistryCommandKey;
  continuous?: boolean;
  continuousInterval?: number;
  defaultValue: T_Maybe<number>;
  deviceId: Device['id'];
  disabled?: boolean;
  label: string;
  onMutation: T_Maybe<(mutation: DeepPartial<Control>) => void>;
  onControlClick?: (controlData: {
    label: string;
    controlType: DeviceRegistryInterfaceItemControlType;
    key: DeviceRegistryCommandKey;
    value?: DeviceRegistryCommandFunctionType;
  }) => void;
  value: number;
}

let interval: NodeJS.Timeout;

/** `<DeviceSettingDialIndeterminate>` component. */
export const DeviceSettingDialIndeterminate = ({
  agent,
  className,
  cmdKey,
  continuous,
  continuousInterval = 250,
  deviceId,
  disabled,
  label,
  onMutation,
  onControlClick
}: DeviceSettingDialProps): ReactElement => {
  const mutate = (act: DeviceRegistryCommandFunctionType): void => {
    const uuid = generateId();

    const control: DeepPartial<Control> = {
      device: {
        [deviceId]: {
          deviceSettings: {
            [String(cmdKey)]: {
              action: {
                action: act,
                uuid
              },
              lastModified: Date.now()
            }
          }
        }
      }
    };

    if (onMutation) {
      onMutation(control);

      return;
    }

    agent
      ?.mutate(
        {
          control
        },
        {}
      )
      .catch(noop);
  };

  const props = {
    className: 'rounded',
    onMinus: (): void => {
      clearInterval(interval);

      interval = setInterval((): void => {
        mutate(DeviceRegistryCommandFunctionType.DOWN);
      }, continuousInterval);

      mutate(DeviceRegistryCommandFunctionType.DOWN);
      onControlClick?.({
        label,
        controlType: DeviceRegistryInterfaceItemControlType.DIAL_INDETERMINATE,
        key: cmdKey,
        value: DeviceRegistryCommandFunctionType.DOWN
      });
    },
    onMouseUp: (): void => {
      clearInterval(interval);

      if (continuous) {
        mutate(DeviceRegistryCommandFunctionType.STOP);
      }
      onControlClick?.({
        label,
        controlType: DeviceRegistryInterfaceItemControlType.DIAL_INDETERMINATE,
        key: cmdKey,
        value: DeviceRegistryCommandFunctionType.STOP
      });
    },
    onPlus: (): void => {
      clearInterval(interval);

      interval = setInterval((): void => {
        mutate(DeviceRegistryCommandFunctionType.UP);
      }, continuousInterval);

      mutate(DeviceRegistryCommandFunctionType.UP);
      onControlClick?.({
        label,
        controlType: DeviceRegistryInterfaceItemControlType.DIAL_INDETERMINATE,
        key: cmdKey,
        value: DeviceRegistryCommandFunctionType.UP
      });
    }
  };

  return (
    <DialIndeterminate.Container className={className} label={label}>
      <DialIndeterminate.Root {...props} disabled={disabled} variant='plus-minus' />
    </DialIndeterminate.Container>
  );
};
